/**
 * api-client — fetch wrapper with cookie-based auth + typed errors.
 *
 * Authorization (post M2):
 *   The browser holds httpOnly `xcrmiq_access` (15 min) + `xcrmiq_refresh` (5 h)
 *   cookies set by /api/v1/auth/login. We send `credentials: 'include'` on
 *   every request so the cookies travel automatically.
 *
 *   On 401, we transparently call /api/v1/auth/refresh once and replay the
 *   request (including `/auth/me` — otherwise an expired access cookie would
 *   look like a full logout). If refresh fails, the caller sees the original 401.
 *
 *   Legacy bearer support: if `setStoredToken` was used (PoW carry-over),
 *   the token is forwarded as `Authorization: Bearer …`. New code should
 *   not use this path; it stays for backward compatibility with code paths
 *   not yet migrated to cookies.
 *
 * Errors:
 *   Non-2xx responses throw an `ApiError`. Network errors and timeouts also
 *   throw `ApiError` so callers (React Query) see a consistent shape.
 */

import { isPlanGateCode, reportPlanGate } from './plan-gate'

const LEGACY_TOKEN_STORAGE_KEY = 'xcrmiq.token'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: unknown
  /**
   * The envelope's structured `error.fields`, carried as sent (unvalidated).
   * Set only when the server sent one, so every other error shape is unchanged.
   * CUSTOM_FIELD_INVALID uses it for per-field problems.
   */
  readonly fields?: unknown
  /**
   * G-38c — the shell already showed this 402 as a plan-gate toast, so a
   * caller's own error toast would only repeat it (see lib/plan-gate.ts).
   */
  planGateReported = false

  constructor(status: number, code: string, message: string, details?: unknown, fields?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
    if (fields !== undefined) this.fields = fields
  }
}

/** True when `err` is a 402 the shell already toasted — skip your own error toast. */
export function isPlanGateReported(err: unknown): boolean {
  return err instanceof ApiError && err.planGateReported
}

const DEFAULT_BASE = '/api/v1'
// console.log("DEFAULT_BASE", import.meta.env.VITE_API_BASE_URL);

const DEFAULT_TIMEOUT_MS = 8_000

function readLegacyToken(): string | null {
  try {
    return typeof window !== 'undefined'
      ? window.localStorage.getItem(LEGACY_TOKEN_STORAGE_KEY)
      : null
  } catch {
    return null
  }
}

/**
 * Legacy carry-over from the localStorage era. Setting `null` clears.
 * Modern login uses cookies — this is only kept so non-cookie callers
 * (mobile, server-to-server) can attach a bearer when they have one.
 */
export function setStoredToken(token: string | null): void {
  try {
    if (typeof window === 'undefined') return
    if (token === null) window.localStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY)
    else window.localStorage.setItem(LEGACY_TOKEN_STORAGE_KEY, token)
  } catch {
    // ignore
  }
}

export function getStoredToken(): string | null {
  return readLegacyToken()
}

/**
 * Registered by the app shell: runs when the user signs out, so cached data
 * (a webhook URL's token, another user's leads) does not outlive the session.
 * A setter, as with the unauthorized handler, to keep this module free of React Query.
 */
let _onSignedOut: (() => void) | null = null
export function setSignedOutHandler(fn: (() => void) | null): void {
  _onSignedOut = fn
}

/** Best-effort logout helper that clears any legacy token + invalidates cookies. */
export async function clearAuth(): Promise<void> {
  setStoredToken(null)
  try {
    await fetch(`${DEFAULT_BASE}/auth/logout`, { method: 'POST', credentials: 'include' })
  } catch {
    // ignore
  }
  _onSignedOut?.()
}

type FetchOptions = Omit<RequestInit, 'body' | 'signal'> & {
  body?: unknown
  baseUrl?: string
  timeoutMs?: number
  signal?: AbortSignal
  /** Bypass the auth header (used by /auth/login). */
  skipAuth?: boolean
  /**
   * 402 plan-gate codes this caller explains inline (see lib/plan-gate.ts);
   * the shell's global "View plans" toast is skipped for them.
   */
  handledPlanGateCodes?: ReadonlyArray<string>
}

/**
 * Optional hook invoked when a request 401s AND the silent refresh fails —
 * i.e. the session is dead. `useAuth` registers `invalidateAuthCache` here so
 * the app re-validates `/auth/me` (correcting a transient blip, or flipping to
 * logged-out and bouncing to login) instead of surfacing a generic
 * "failed to load" on every data hook. Registered via a setter to avoid a
 * circular import (useAuth already imports this module).
 */
let _onUnauthorized: (() => void) | null = null
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  _onUnauthorized = fn
}

/** What a session renewal came to (audit C1, C4). */
type Renewal = 'renewed' | 'signed-out' | 'unavailable'

let _renewalInFlight: Promise<Renewal> | null = null

let _onBearerRejected: ((token: string) => void) | null = null

/**
 * The impersonation store registers here (audit B2). Called with the stored
 * bearer the server just rejected (ended elsewhere, the operator signed out,
 * or its 30 minutes ran out) so the store can drop its banner state if that
 * impersonation is still the current one.
 */
export function setBearerRejectedHandler(fn: ((token: string) => void) | null): void {
  _onBearerRejected = fn
}

/**
 * How long to wait after losing a refresh race before asking again: the
 * request that won is setting the new cookies, and its response may still be
 * on its way (audit C1).
 */
const RACE_SETTLE_MS = 250

/**
 * Refresh calls one renewal makes while another tab's win settles. Three
 * calls span about half a second, far inside the server's 30-second race
 * window, so asking again can never be taken for token reuse.
 */
const RACE_ATTEMPTS = 3

async function errorCodeOf(res: Response): Promise<string | undefined> {
  try {
    return ((await res.json()) as { error?: { code?: string } }).error?.code
  } catch {
    return undefined
  }
}

/**
 * Renew the access cookie. Coalesced, so concurrent 401s in this tab make one
 * call. Another tab can still race it: the server then answers 409
 * REFRESH_RACE, meaning the other tab renewed (audit C1). Its new cookies
 * reach this tab only when its response lands, so this asks again rather than
 * assume they have: retrying the request on the old access cookie would 401,
 * and on /auth/me that reads as signed out. A 429 or a server/network failure
 * means renewal couldn't happen just now, not that the session is over, so it
 * isn't read as signed out (audit C4); nor is a race that hasn't settled.
 */
async function renewSession(): Promise<Renewal> {
  if (_renewalInFlight) return _renewalInFlight
  _renewalInFlight = (async (): Promise<Renewal> => {
    try {
      for (let attempt = 1; ; attempt += 1) {
        const res = await fetch(`${DEFAULT_BASE}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        })
        if (res.ok) return 'renewed'
        if (res.status === 409 && (await errorCodeOf(res)) === 'REFRESH_RACE') {
          if (attempt === RACE_ATTEMPTS) return 'unavailable'
          await new Promise((settle) => setTimeout(settle, RACE_SETTLE_MS))
          continue
        }
        return res.status === 429 || res.status >= 500 ? 'unavailable' : 'signed-out'
      }
    } catch {
      return 'unavailable'
    } finally {
      _renewalInFlight = null
    }
  })()
  return _renewalInFlight
}

/**
 * Renew the access cookie; true when it worked.
 *
 * Exported (M-COP W1) so the copilot SSE client — which bypasses apiFetch
 * because of the 8s timeout and JSON-only contract — reuses the SAME
 * coalesced refresh on its one 401 retry instead of racing a second one.
 */
export async function refreshAccess(): Promise<boolean> {
  return (await renewSession()) === 'renewed'
}

/**
 * What a caller gets when its request needed a renewal that couldn't happen
 * just now: a 503 it can retry, not a 401 that reads as signed out (audit C4).
 */
function renewalUnavailable(): Response {
  return new Response(
    JSON.stringify({
      ok: false,
      error: { code: 'SESSION_RENEWAL_UNAVAILABLE', message: "Couldn't renew your session just now. Try again in a moment." },
    }),
    { status: 503, headers: { 'content-type': 'application/json' } },
  )
}

/**
 * The stored bearer a request will carry, read once when it is sent: null for
 * cookie-only requests, skipAuth calls and callers that set Authorization
 * themselves.
 */
function bearerFor(opts: FetchOptions): string | null {
  if (opts.skipAuth || new Headers(opts.headers ?? undefined).has('Authorization')) return null
  return readLegacyToken()
}

async function performFetch(
  path: string,
  baseUrl: string,
  opts: FetchOptions,
  bearer: string | null,
): Promise<Response> {
  const headers = new Headers(opts.headers ?? undefined)
  if (!headers.has('Content-Type') && opts.body !== undefined) {
    headers.set('Content-Type', 'application/json')
  }
  if (bearer) headers.set('Authorization', `Bearer ${bearer}`)

  const ctrl = new AbortController()
  if (opts.signal) {
    if (opts.signal.aborted) ctrl.abort(opts.signal.reason)
    else
      opts.signal.addEventListener('abort', () => ctrl.abort(opts.signal?.reason), {
        once: true,
      })
  }
  const timeoutId = setTimeout(
    () => ctrl.abort(new ApiError(0, 'TIMEOUT', `Request to ${path} timed out`)),
    opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
  )

  try {
    return await fetch(`${baseUrl}${path}`, {
      ...opts,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: ctrl.signal,
      // Always include cookies — that's how httpOnly access/refresh travel.
      credentials: opts.credentials ?? 'include',
    })
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Compile-time guard against unwrapping the envelope twice.
 *
 * `apiFetch` returns the envelope's `data`, never the envelope itself. Asking
 * it for an envelope-shaped type and then reading `.data` off the result
 * unwraps twice: it typechecks perfectly and evaluates to `undefined` at
 * runtime, so the feature goes quietly inert while tests and `tsc` stay green.
 *
 * That exact mistake shipped three times — lead import, WhatsApp onboarding
 * and Twilio WhatsApp — each behind a flag, each undetectable without a
 * browser. Rather than fix three instances, resolve an envelope-shaped request
 * to a hint string, which makes every future occurrence a compile error at the
 * call site that says what to do instead. Per development-policy §5 (fix the
 * class) and §7 (prefer "cannot happen" to "is checked").
 */
export type Payload<T> = T extends { ok: unknown; data: unknown }
  ? 'apiFetch already unwraps { ok, data } — ask for the payload type, not the envelope'
  : T

export async function apiFetch<T>(path: string, opts: FetchOptions = {}): Promise<Payload<T>> {
  const response = await fetchWithRefresh(path, opts)

  if (response.status === 204) return undefined as Payload<T>

  let payload: unknown
  try {
    payload = await response.json()
  } catch {
    // express-rate-limit / nginx HTML bodies used to surface as a useless
    // "Non-JSON response from /auth/login". Prefer a status-aware message.
    const code = response.status === 429 ? 'RATE_LIMITED' : 'INVALID_JSON'
    const message =
      response.status === 429
        ? 'Too many requests. Please wait a few minutes and try again.'
        : `Non-JSON response from ${path} (HTTP ${response.status})`
    throw new ApiError(response.status, code, message)
  }

  if (!response.ok) {
    const errEnvelope = payload as { error?: { code?: string; message?: string; details?: unknown; fields?: unknown } }
    const code = errEnvelope.error?.code ?? `HTTP_${response.status}`
    const message = errEnvelope.error?.message ?? response.statusText
    const err = new ApiError(response.status, code, message, errEnvelope.error?.details, errEnvelope.error?.fields)
    // audit A1 — this session is limited to 2FA enrolment (a refresh can
    // restrict one that was full). Re-check /auth/me so the app shows the
    // enrolment page instead of a page error. /auth/me itself is allowed, so
    // this can't loop.
    if (response.status === 403 && code === 'TWO_FACTOR_ENROLMENT_REQUIRED') {
      try {
        _onUnauthorized?.()
      } catch {
        /* never let the handler break the response path */
      }
    }
    if (response.status === 402 && isPlanGateCode(code) && !opts.handledPlanGateCodes?.includes(code)) {
      err.planGateReported = reportPlanGate({ code, message, details: errEnvelope.error?.details })
    }
    throw err
  }

  const envelope = payload as { ok?: boolean; data?: unknown }
  if (envelope && envelope.ok === true && 'data' in envelope) {
    return envelope.data as Payload<T>
  }
  return payload as Payload<T>
}

/**
 * The raw `Response` for `path`, with the same auth handling as `apiFetch` (cookies, legacy
 * bearer, one transparent refresh-and-retry on 401). For non-JSON bodies such as file downloads;
 * the caller checks `response.ok` and reads the body itself.
 */
export async function fetchWithRefresh(path: string, opts: FetchOptions = {}): Promise<Response> {
  const baseUrl =
    opts.baseUrl ?? (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? DEFAULT_BASE

  // Fixed for the request's life, so the retry below goes out as the same
  // person even if an impersonation starts or ends meanwhile (audit B2).
  const bearer = bearerFor(opts)
  let response: Response
  try {
    response = await performFetch(path, baseUrl, opts, bearer)
  } catch (err) {
    if (err instanceof ApiError) throw err
    throw new ApiError(0, 'NETWORK', err instanceof Error ? err.message : 'Network failure')
  }

  // Transparent refresh-and-retry on 401. Skip only the refresh call itself
  // (and callers that set skipAuth — login/register/forgot/etc.) so `/auth/me`
  // can recover from an expired 15-min access cookie while the 5h session lives.
  const barePath = (path.split('?')[0] ?? path).replace(/\/$/, '') || path
  const isRefreshCall = barePath === '/auth/refresh'
  if (response.status === 401 && !opts.skipAuth && !isRefreshCall) {
    if (bearer !== null) {
      // A rejected bearer is never refreshed and retried. The refresh renews
      // only the cookie, so the retry would resend the dead bearer or, sent as
      // the cookie, run as the operator instead of the customer. Clear the
      // bearer only if it is still the stored one: a newer impersonation may
      // have replaced it while this request was in flight.
      if (readLegacyToken() === bearer) setStoredToken(null)
      _onBearerRejected?.(bearer)
      if (barePath !== '/auth/me') {
        try {
          _onUnauthorized?.()
        } catch {
          /* never let the handler break the response path */
        }
      }
      return response
    }
    const renewal = await renewSession()
    if (renewal === 'renewed') {
      try {
        response = await performFetch(path, baseUrl, opts, bearer)
      } catch (err) {
        if (err instanceof ApiError) throw err
        throw new ApiError(0, 'NETWORK', err instanceof Error ? err.message : 'Network failure')
      }
    } else if (renewal === 'unavailable') {
      return renewalUnavailable()
    } else if (barePath !== '/auth/me') {
      // Refresh failed → the session is (probably) dead. Ask the app to
      // re-validate auth: a transient blip self-corrects via /auth/me, a real
      // expiry flips to logged-out + redirects to login. This turns a stack of
      // generic "failed to load" cards into a clean re-auth prompt. The
      // original 401 still throws below so the in-flight caller settles.
      //
      // NEVER for `/auth/me` itself: /auth/me IS the re-validation call, so
      // firing the handler here recurses — the handler re-polls /auth/me, which
      // 401s, refresh fails, handler fires again — hammering /auth/me +
      // /auth/refresh and remounting the whole app in a tight loop (the logged-
      // out landing page flickers as isLoading toggles). The /auth/me caller
      // (loadMe) already treats a 401 as "logged out", so no re-validation is
      // needed or wanted on this path.
      try {
        _onUnauthorized?.()
      } catch {
        /* never let the handler break the response path */
      }
    }
  }
  return response
}
